import React, { useMemo } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message, Typography } from "@mimber/ui-web"
import { Form, InputField, SelectField, DatePickerField } from "@mimber/forms"
import {
  useAddGroupMember,
  useGroupRoles,
  useGroups,
  useGroupDues,
} from "@mimber/client/mimber-organization"
import { formatCurrency } from "@/utils"
import type { AddMemberToGroupFormProps } from "./AddMemberToGroupForm.types"
import {
  addMemberToGroupSchema,
  type AddMemberToGroupData,
} from "./AddMemberToGroupForm.schema"

const { Text } = Typography

export const AddMemberToGroupForm: React.FunctionComponent<
  AddMemberToGroupFormProps
> = ({ memberUuid, onSuccess }) => {
  const form = useForm<AddMemberToGroupData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(addMemberToGroupSchema) as any,
    mode: "onBlur",
    defaultValues: {
      groupUuid: "",
      groupRoleUuid: undefined,
      title: undefined,
      duesFirstBillingDate: undefined,
    },
  })

  const selectedGroupUuid = form.watch("groupUuid")

  const { reset } = form

  const { data: groupsResponse, isLoading: groupsLoading } = useGroups({
    limit: 100,
  })

  const { data: rolesResponse, isLoading: rolesLoading } = useGroupRoles(
    selectedGroupUuid || "",
    { limit: 100 }
  )

  const { data: duesResponse } = useGroupDues(selectedGroupUuid || "")
  const dues = duesResponse?.data
  const hasActiveDues =
    !!dues?.duesIsActive && !!dues.duesPrice && !!dues.duesIntervalType

  const groupOptions = useMemo(() => {
    if (!groupsResponse?.data) return []

    return groupsResponse.data.map((group) => ({
      label: group.name,
      value: group.uuid,
    }))
  }, [groupsResponse])

  const roleOptions = useMemo(() => {
    if (!rolesResponse?.data) return []

    return rolesResponse.data.map((role) => ({
      label: role.name,
      value: role.uuid,
    }))
  }, [rolesResponse])

  const mutation = useAddGroupMember(selectedGroupUuid || "", {
    onSuccess: (response) => {
      message.success("Member added to group successfully")
      reset()
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to add member to group: ${error.message}`)
    },
  })

  const onSubmit = (data: AddMemberToGroupData) => {
    mutation.mutate({
      membershipUuid: memberUuid,
      groupRoleUuid: data.groupRoleUuid,
      title: data.title,
      duesFirstBillingDate: hasActiveDues
        ? data.duesFirstBillingDate
        : undefined,
    })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <SelectField
        name="groupUuid"
        label="Group"
        placeholder="Select a group"
        required
        options={groupOptions}
        loading={groupsLoading}
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />

      <SelectField
        name="groupRoleUuid"
        label="Role"
        placeholder="Select a role (optional)"
        options={roleOptions}
        loading={rolesLoading}
        allowClear
        disabled={!selectedGroupUuid}
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />

      <InputField
        name="title"
        label="Title"
        placeholder="e.g., Secretary, Treasurer"
        maxLength={255}
        showCount
      />

      {hasActiveDues && dues && (
        <>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            This group has dues of {formatCurrency(dues.duesPrice)} every{" "}
            {dues.duesIntervalLength ?? 1}{" "}
            {dues.duesIntervalType!.toLowerCase()}.
          </Text>

          <DatePickerField
            name="duesFirstBillingDate"
            label="First Billing Date"
            help="Defaults to today if left blank. Anchors both the first invoice and the recurring billing cycle."
          />
        </>
      )}

      <Space style={{ marginTop: 16 }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}
          disabled={!selectedGroupUuid}
        >
          Add to Group
        </Button>
      </Space>
    </Form>
  )
}
